import React, { useEffect, useRef, useState } from "react";
import { X, Mail, Lock, Loader2 } from "lucide-react";

/**
 * One-step email gate before Stripe redirect.
 * Captures the email so we can recover abandoned carts.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - onContinue: (email: string, name: string) => Promise<void>  // calls /api/checkout/intent then /checkout/session
 *  - summary: { qty, totalPillows, displayedPrice, finalPrice, height, color, hasDiscount }
 */
export default function PreCheckoutEmailModal({ open, onClose, onContinue, summary }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem("dp_email") || "";
      setEmail(saved);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/.+@.+\..+/.test(email)) return;
    try {
      setLoading(true);
      localStorage.setItem("dp_email", email);
      await onContinue(email.trim().toLowerCase(), name.trim());
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="pre-checkout-modal"
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white w-full max-w-md rounded-[28px] shadow-2xl overflow-hidden">
        <div className="relative p-6 md:p-8">
          <button
            type="button"
            onClick={onClose}
            data-testid="pre-checkout-close"
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-black/5 inline-flex items-center justify-center text-[#0e0e10]/60"
          >
            <X size={18} />
          </button>

          <div className="sticker sticker-lime inline-block text-[11px] mb-4">
            Almost there
          </div>
          <h2 className="text-[24px] md:text-[28px] font-black text-[#0e0e10] tracking-tight leading-tight">
            Where should we send
            <br />
            your confirmation?
          </h2>
          <p className="mt-2 text-[13px] text-[#5a5a60]">
            Order ID, tracking link & receipt — straight to your inbox.
            <span className="inline-flex items-center gap-1 ml-1"><Lock size={11} /> Never shared.</span>
          </p>

          {summary && (
            <div className="mt-5 rounded-2xl bg-[#fafaf9] border border-black/5 p-4 text-[12px]">
              <div className="flex justify-between">
                <span className="text-[#5a5a60]">
                  {summary.totalPillows}× Dubai Pillow · {summary.height} · {summary.color}
                </span>
                <span className="font-black tabular-nums text-[#0e0e10]">
                  ${summary.hasDiscount ? summary.finalPrice.toFixed(2) : summary.displayedPrice.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">Email</label>
              <div className="mt-1 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8a8a]" size={16} />
                <input
                  ref={inputRef}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="pre-checkout-email"
                  className="w-full rounded-full border border-black/15 pl-10 pr-5 py-3.5 text-[14px] focus:outline-none focus:border-[#0e0e10]"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">Name <span className="text-[#aaa] normal-case">(optional)</span></label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex"
                data-testid="pre-checkout-name"
                className="mt-1 w-full rounded-full border border-black/15 px-5 py-3.5 text-[14px] focus:outline-none focus:border-[#0e0e10]"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !email}
              data-testid="pre-checkout-continue"
              className="pill-btn w-full py-4 text-[13px] disabled:opacity-60 mt-2"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Securing your order…</span>
              ) : (
                "Continue to secure checkout →"
              )}
            </button>
            <p className="text-center text-[10px] text-[#8a8a8a] mt-2 inline-flex items-center justify-center gap-1.5 w-full">
              <Lock size={10} /> 256-bit SSL · Powered by Stripe
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
